import React, { useEffect, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import { useLanguage } from '../../i18n/LanguageContext';
import DistributorPurchaseHistory from './DistributorPurchaseHistory';
import { displayFamilyName } from './distributorBankSummary';
import CashBankBoundary from '../distributor-cash/CashBankBoundary';
import './DistributorBank.css';

const validHistory = (value) =>
  Array.isArray(value?.data) &&
  Number.isSafeInteger(value.total) &&
  value.total >= value.data.length &&
  value.data.length <= 10 &&
  value.data.every(
    (entry) =>
      typeof entry?._id === 'string' &&
      entry._id.length > 0 &&
      /^[a-z][a-z0-9_]{1,39}$/.test(entry.familyKey) &&
      ['purchase', 'correction', 'debit', 'reversal'].includes(entry.kind) &&
      Number.isSafeInteger(entry.unitsDelta) &&
      entry.unitsDelta !== 0 &&
      (entry.createdAt === undefined || Number.isFinite(Date.parse(entry.createdAt))) &&
      ['orderId', 'purchaseId'].every(
        (key) => entry[key] === undefined || typeof entry[key] === 'string'
      )
  );

export default function DistributorBank({ t: providedT, onModeChange, readOnly = false }) {
  const { t: languageT } = useLanguage();
  const t = providedT || languageT;
  const [historyOpen, setHistoryOpen] = useState(false);
  return (
    <section className="me-dw-bank" aria-label={t('Tissue bank')}>
      <CashBankBoundary {...{ onModeChange, readOnly }} />
      <details
        className="me-cash me-cash-card me-cash-details me-dw-bank-history"
        onToggle={(event) => setHistoryOpen(event.currentTarget.open)}
      >
        <summary>{t('Historical unit records')}</summary>
        <p>
          {t(
            'Historical product units are separate from Virtual Tissue Bank Balance and are not converted to dollars.'
          )}
        </p>
        {historyOpen && <HistoricalUnitRecords t={t} />}
      </details>
    </section>
  );
}

function HistoricalUnitRecords({ t }) {
  const { locale } = useLanguage();
  const [page, setPage] = useState(1);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const { begin, version } = useShippingRequest(`historical-unit-records:${page}`);
  useEffect(() => {
    const request = begin();
    setHistory(null);
    setError(false);
    Promise.resolve(distributorService.listBankEntries('', page))
      .then((response) => {
        if (!request.isCurrent()) return;
        const value = response?.data?.data;
        if (!validHistory(value)) throw new Error('Invalid historical records');
        setHistory(value);
      })
      .catch(() => {
        if (request.isCurrent()) setError(true);
      });
  }, [begin, version, page, retry]);
  return (
    <div className="me-cash">
      <DistributorPurchaseHistory />
      <section aria-label={t('Historical unit activity')}>
        <h2 className="me-dw-bank-section-title">{t('Historical unit activity')}</h2>
        {error && (
          <p role="alert">
            {t('Bank history is unavailable. Please try again.')}{' '}
            <button type="button" onClick={() => setRetry((value) => value + 1)}>
              {t('Retry')}
            </button>
          </p>
        )}
        {!history && !error && <p role="status">{t('Loading bank history...')}</p>}
        {history?.data.length === 0 && <p>{t('No unit activity yet.')}</p>}
        {history?.data.length > 0 && (
          <ul className="me-dw-list">
            {history.data.map((entry) => (
              <li key={entry._id} className="flex flex-wrap justify-between gap-2 p-3 text-sm">
                <span>
                  {displayFamilyName(entry.familyKey)} {'\u00b7'} {t(entry.kind)} {'\u00b7'}{' '}
                  {entry.createdAt && new Date(entry.createdAt).toLocaleString(locale)}
                  <details className="me-dw-bank-refs">
                    <summary>{t('References')}</summary>
                    <span>
                      {t('Bank entry ID')}: {entry._id}
                    </span>
                    {entry.orderId && (
                      <span className="block break-all">
                        {t('Order ID')}: {entry.orderId}
                      </span>
                    )}
                    {entry.purchaseId && (
                      <span className="block break-all">
                        {t('Purchase ID')}: {entry.purchaseId}
                      </span>
                    )}
                  </details>
                </span>
                <strong>
                  {entry.unitsDelta > 0 ? '+' : ''}
                  {entry.unitsDelta.toLocaleString(locale)} {t('units')}
                </strong>
              </li>
            ))}
          </ul>
        )}
        {history?.total > 10 && (
          <div className="me-cash-actions">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((value) => value - 1)}
            >
              {t('Previous')}
            </button>
            <span>
              {t('Page {page} of {pages}', { page, pages: Math.ceil(history.total / 10) })}
            </span>
            <button
              type="button"
              disabled={page * 10 >= history.total}
              onClick={() => setPage((value) => value + 1)}
            >
              {t('Next')}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
